import { useCookies } from '@vueuse/integrations/useCookies';
import { useUserStore } from '@/stores/user';
import { isGtmInlineScript } from '@/utils/googleUtils';
import { sleep } from './sleep';

declare global {
    interface Window {
        dataLayer: any[];
    }
}

export const loadUpConsentPlugin = (callback: (isResolved: boolean) => void) => {
    const url = import.meta.env.VITE_CONSENT_PLUGIN_URL;
    const scripts = document.querySelectorAll('script');

    const isPresent = Array.from(scripts).some((s) => s.src === url);

    // check script present
    if (isPresent) {
        return;
    }

    const script = document.createElement('script');
    script.src = url;
    script.async = true;

    script.onload = async () => {
        if (!callback || typeof callback !== 'function') {
            return;
        }

        let attempts = 0;
        const sleepTime = 20;
        const maxAttempt = 500;

        while (attempts <= maxAttempt) {
            if (document.querySelector('#termsfeed-pc1-notice-banner')) {
                return callback(false);
            }

            await sleep(sleepTime);
            attempts++;
        }

        callback(false);
    };

    script.onerror = () => {
        console.error('Failed to load consent plugin!');
        callback(true);
    };

    const firstMeta = document.getElementsByTagName('meta')[0];
    document.head.insertBefore(script, firstMeta);
};

export const changeConsentTheme = (mode = 'light') => {
    const cls = {
        dark: 'termsfeed-pc1-color-palette-dark',
        light: 'termsfeed-pc1-color-palette-light',
    };
    if (!cls[mode]) console.error('No theme for such key, switching to light mode consent. Key: ', mode);

    const consent = document.querySelector('#termsfeed-pc1-notice-banner');
    const overview = document.querySelector('#termsfeed-pc1-preferences-center');

    if (!consent) console.warn('Failed to find consent banner!');
    if (!overview) console.warn('Failed to find overview!');

    if (consent) {
        consent.classList.remove(cls.dark);
        consent.classList.remove(cls.light);
        consent.classList.add(cls[mode] ?? cls.light);
    }
    if (overview) {
        overview.classList.remove(cls.dark);
        overview.classList.remove(cls.light);
        overview.classList.add(cls[mode] ?? cls.light);
    }
};

export const registerDetailEvent = (themeMode: string) => {
    const el = document.querySelector('#termsfeed_privacy_consent_notice_banner_open_privacy_preferences');
    if (!el) return console.warn('Failed to register click event for consent overview');

    const onClick = async () => {
        let attempts = 0;
        const sleepTime = 20;
        const maxAttempt = 500;

        while (attempts <= maxAttempt) {
            if (document.querySelector('#termsfeed-pc1-preferences-center')) {
                changeConsentTheme(themeMode);
            }
            await sleep(sleepTime);
            attempts++;
        }
    };

    // Remove previous event listener
    el.removeEventListener('click', onClick);

    // Add new event listener
    el.addEventListener('click', onClick);
};

export const runScript = (script, forceExecute = false, runOnce?: boolean, scriptId: string = '-1', dataLayerData: any = {}) => {
    const cookies = useCookies(['termsfeed_pc1_vendors_accepted']);
    const userStore = useUserStore();

    try {
        const cookieConsent = cookies.get('termsfeed_pc1_vendors_accepted')?.enabled_vendors.length;
        if (cookieConsent || forceExecute) {
            if (!script || userStore.getHtmlScriptExecuted(scriptId)) {
                return;
            }

            // Function to check if a script source exists in the DOM
            const scriptExists = (src: string) => Array.from(document.getElementsByTagName('script')).some((script) => script.src === src);

            // Function to inject a script into the DOM
            const injectScript = (src: string, callback: () => void) => {
                const scriptHeadElement = document.createElement('script');
                scriptHeadElement.src = src;
                scriptHeadElement.async = true;
                scriptHeadElement.onload = () => {
                    callback();

                    // Push purchase event after GTM loads
                    if (dataLayerData?.items?.length > 0 && isGtmInlineScript(scriptHeadElement.src)) {
                        window.dataLayer = window.dataLayer || [];
                        window.dataLayer.push({
                            event: dataLayerData.items[0]?.price ? 'purchase' : 'order',
                            ecommerce: Object.fromEntries(
                                Object.entries({
                                    transaction_id: dataLayerData.transaction_id,
                                    value: dataLayerData.value,
                                    currency: dataLayerData.currency,
                                    branch: dataLayerData.branch,
                                    email: dataLayerData.email,
                                    items: dataLayerData.items[0]
                                        ? [
                                              Object.fromEntries(
                                                  Object.entries({
                                                      item_id: dataLayerData.items[0].item_id,
                                                      item_name: dataLayerData.items[0].item_name,
                                                      item_brand: dataLayerData.items[0].item_brand,
                                                      price: dataLayerData.items[0].price,
                                                      quantity: dataLayerData.items[0].quantity,
                                                  }).filter(([_, v]) => v !== null && v !== '')
                                              ),
                                          ]
                                        : [],
                                }).filter(([_, v]) => v !== null && v !== '' && (!Array.isArray(v) || v.length > 0)) // Remove empty arrays too
                            ),
                        });
                    }
                };

                document.head.insertBefore(scriptHeadElement, document.head.firstChild);

                // Google Tag Manager
                if (isGtmInlineScript(src)) {
                    const gtmId = src.match(/id=(GTM-[\w\d]+)/)?.[1] || '';

                    // Create the iframe element
                    const scriptBodyElement = document.createElement('iframe');
                    scriptBodyElement.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
                    scriptBodyElement.height = '0';
                    scriptBodyElement.width = '0';
                    scriptBodyElement.style.display = 'none';
                    scriptBodyElement.style.visibility = 'hidden';

                    // Append the iframe inside the noscript element
                    const noscriptElement = document.createElement('noscript');
                    noscriptElement.appendChild(scriptBodyElement);
                    document.body.insertBefore(noscriptElement, document.body.firstChild);
                }
            };

            const decodedScript = decodeURIComponent(script);
            const srcRegex = /src="([^"]+)"/;

            // Extract only the scripts from the decoded script
            const scripts = decodedScript.match(/<script\b[^>]*>([\s\S]*?)<\/script>/gi);

            const inlineScripts = [];
            let scriptsLoaded = 0;
            const totalScripts = scripts.filter((script) => srcRegex.test(script)).length;

            const onScriptLoad = () => {
                if (++scriptsLoaded === totalScripts) {
                    setTimeout(() => inlineScripts.forEach(eval), 100);
                }
            };

            // Process each script block
            scripts.forEach((script: string) => {
                const matchSrc = script.match(srcRegex);

                if (matchSrc) {
                    const sourceUrl = matchSrc[1];
                    if (!scriptExists(sourceUrl)) {
                        injectScript(sourceUrl, onScriptLoad);
                    } else {
                        onScriptLoad();
                    }
                } else {
                    // Extract the inline script content
                    const inlineScriptExtracted = script.replace(/<script\b[^>]*>/, '').replace(/<\/script>/, '');

                    const inlineScriptCleaned = inlineScriptExtracted
                        .trim()
                        .replace(/(\r\n|\n|\r)/gm, '')
                        .trim();

                    if (inlineScriptCleaned) {
                        inlineScripts.push(inlineScriptCleaned);

                        // Check if it's a GTM script and execute it
                        if (isGtmInlineScript(inlineScriptCleaned)) {
                            // Optionally load the GTM script if not already done
                            const gtmIdMatch = inlineScriptCleaned.match(/GTM-\w+/);
                            if (gtmIdMatch) {
                                const gtmId = gtmIdMatch[0];
                                const gtmScriptUrl = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
                                if (!scriptExists(gtmScriptUrl) || userStore.getGtmId) {
                                    injectScript(gtmScriptUrl, onScriptLoad);
                                }
                            }
                        }
                    }
                }
            });

            if (totalScripts === 0) {
                setTimeout(() => {
                    inlineScripts.forEach((inlineScript) => eval(inlineScript));
                }, 100);
            }

            if (runOnce) {
                userStore.setHtmlScriptExecuted(true, scriptId);
            }
        }
    } catch (error) {
        console.error(error);
    }
};

export const insertGtmDirectly = (gtmId: string) => {
    const scriptHeadElement = document.createElement('script');
    scriptHeadElement.innerHTML = `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${gtmId}');
    `;
    document.head.insertBefore(scriptHeadElement, document.head.firstChild);

    const noscriptElement = document.createElement('noscript');
    const scriptBodyElement = document.createElement('iframe');
    scriptBodyElement.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
    scriptBodyElement.height = '0';
    scriptBodyElement.width = '0';
    scriptBodyElement.style.display = 'none';
    scriptBodyElement.style.visibility = 'hidden';

    noscriptElement.appendChild(scriptBodyElement);
    document.body.insertBefore(noscriptElement, document.body.firstChild);
};
